/**
 * Analytics, My Agents and the shared-chat page.
 * Keys are namespaced ('pages.*'); the Arabic table must cover every key.
 */
const en = {
  // ── Analytics: header & controls ─────────────────────────────────
  'pages.analytics.title': 'Analytics',
  'pages.analytics.subtitle': 'Platform usage, agent adoption, and feedback',
  'pages.analytics.updated': 'Updated {time}',
  'pages.analytics.timeRange': 'Time range',
  'pages.analytics.range7': 'Last 7 days',
  'pages.analytics.range14': 'Last 14 days',
  'pages.analytics.range30': 'Last 30 days',
  'pages.analytics.range90': 'Last 90 days',
  'pages.analytics.range365': 'Last year',
  'pages.analytics.refresh': 'Refresh analytics',
  'pages.analytics.refreshHint': 'Refresh (bypasses the 1-minute server cache)',
  'pages.analytics.loadFailed': 'Failed to load analytics data.',
  'pages.analytics.noData': 'No analytics available.',

  'pages.analytics.noMatches': 'No matches for “{q}”.',

  // ── KPI tiles ────────────────────────────────────────────────────
  'pages.analytics.kpiActiveUsers': 'Active users',
  'pages.analytics.kpiActiveUsersSub': 'of {total} total ({pct})',
  'pages.analytics.kpiMessages': 'Messages',
  'pages.analytics.kpiMessagesSub': '{n} agent replies',
  'pages.analytics.kpiSatisfaction': 'Satisfaction',
  'pages.analytics.kpiSatisfactionSub': '{pos} positive · {neg} negative',
  'pages.analytics.deltaNew': 'New',
  'pages.analytics.deltaPts': '{n} pts',
  'pages.analytics.deltaHint': 'Change vs. the previous period of the same length',
  'pages.analytics.deltaSr': ' vs. previous period',

  // ── Panels & charts ──────────────────────────────────────────────
  'pages.analytics.activityTitle': 'Activity over time',
  'pages.analytics.activityAria': 'Line chart of daily messages, sessions and active users',
  'pages.analytics.noActivity': 'No activity in this range.',
  'pages.analytics.seriesMessages': 'Messages',
  'pages.analytics.seriesSessions': 'Sessions',
  'pages.analytics.seriesActiveUsers': 'Active users',
  'pages.analytics.heatmapTitle': 'When people use it',
  'pages.analytics.localTime': 'Your local time',
  'pages.analytics.departmentsTitle': 'Usage by department',
  'pages.analytics.departmentsAria': 'Bar chart of messages and active users per department',
  'pages.analytics.noDepartments': 'No department activity in this range.',
  'pages.analytics.deptActiveOf': '{active} of {total} users active',

  // ── Agent adoption & user activity tables ────────────────────────
  'pages.analytics.adoptionTitle': 'Agent adoption',
  'pages.analytics.noAgentsUsed': 'No agents used in this range.',
  'pages.analytics.colAgent': 'Agent',
  'pages.analytics.colUser': 'User',
  'pages.analytics.colRole': 'Role',
  'pages.analytics.colMessages': 'Messages',
  'pages.analytics.colSessions': 'Sessions',
  'pages.analytics.colUsers': 'Users',
  'pages.analytics.colSatisfaction': 'Satisfaction',
  'pages.analytics.colLastUsed': 'Last Used',
  'pages.analytics.colLastAgent': 'Last Agent',
  'pages.analytics.colLastActivity': 'Last Activity',
  'pages.analytics.ratings': '{n} ratings',
  'pages.analytics.nounAgents': 'agents',
  'pages.analytics.nounUsers': 'users',
  'pages.analytics.userActivityTitle': 'User activity',
  'pages.analytics.allTime': 'All time',
  'pages.analytics.searchUsers': 'Search users...',
  'pages.analytics.searchUsersLabel': 'Search users',
  'pages.analytics.loadingUsers': 'Loading user activity',
  'pages.analytics.noUsersMatch': 'No users matching your search.',
  'pages.analytics.noUserData': 'No user data available.',
  'pages.analytics.details': 'Details',
  'pages.analytics.detailsHint': 'View agent usage breakdown',
  'pages.analytics.detailsAria': 'View agent usage for {name}',
  'pages.analytics.roleAdmin': 'Admin',
  'pages.analytics.roleUser': 'User',

  // ── Per-user usage modal ─────────────────────────────────────────
  'pages.analytics.usageModalTitle': 'Agent Usage — {name}',
  'pages.analytics.loadingAgentUsage': 'Loading agent usage',
  'pages.analytics.noAgentActivity': 'This user has no agent activity yet.',
  'pages.analytics.agentsUsed': 'Agents Used',
  'pages.analytics.accessRevoked': 'access revoked',
  'pages.analytics.notUsedYet': 'not used yet',
  'pages.analytics.usageMeta': '{messages} messages · {sessions} sessions',
  'pages.analytics.lastUsed': 'Last used',

  // ── Heatmap ──────────────────────────────────────────────────────
  'pages.analytics.heatmapEmpty': 'No messages in this range.',
  'pages.analytics.heatmapBusiest': 'Busiest:',
  'pages.analytics.heatmapPeakCount': '({n} messages)',
  'pages.analytics.heatmapCaption': 'Messages by weekday and hour of day',
  'pages.analytics.heatmapDay': 'Day',
  'pages.analytics.heatmapCell': '{day} {hour} — {n} messages',
  'pages.analytics.heatmapLess': 'Less',
  'pages.analytics.heatmapMore': 'More',

  // ── Usage alerts ─────────────────────────────────────────────────
  'pages.analytics.alertsTitle': 'Usage alerts',
  'pages.analytics.alertNearLimit': '{used} of {limit} messages used today (UTC)',
  'pages.analytics.alertSpike': '{n} messages in 24h — {ratio}× the usual {avg}/day',
  'pages.analytics.alertSpikeNoBaseline': '{n} messages in 24h with no earlier activity in this range',
  'pages.analytics.kindAgent': 'Agent',
  'pages.analytics.kindUser': 'User',

  // ── Spend: agent and scheduled-task cost sources ─────────────────
  'pages.analytics.costsUnavailable': 'Agent cost reports could not be loaded.',
  'pages.analytics.costsNotConfigured': 'Agent cost reports are not configured (AgentCosts:Bucket).',
  'pages.analytics.costsAccessDenied': "The server doesn't have permission to read the agent cost reports in S3.",
  'pages.analytics.costsNoteSar': ' SAR is converted at {rate} SAR per USD.',
  'pages.analytics.costsCurrency': 'Currency',
  'pages.analytics.costsByAgent': 'By agent',
  'pages.analytics.costsByUser': 'By user',
  'pages.analytics.costsByDepartment': 'By department',
  'pages.analytics.colDepartment': 'Department',
  'pages.analytics.nounDepartments': 'departments',
  'pages.analytics.spendNoDepartment': 'No department',
  'pages.analytics.spendDepartmentSub': '{n} users with spend',
  'pages.analytics.costsByModel': 'By model',
  'pages.analytics.colModel': 'Model',
  'pages.analytics.colAgents': 'Agents',
  'pages.analytics.colRequests': 'Requests',
  'pages.analytics.colTokens': 'Tokens',
  'pages.analytics.colCost': 'Cost',
  'pages.analytics.nounModels': 'models',
  'pages.analytics.schedCostsUnpriced': 'No price is set for {models}. Their tokens are counted but cost 0. Add them under BedrockModels:Pricing.',
  'pages.analytics.schedCostsByTask': 'By task',
  'pages.analytics.colTask': 'Task',
  'pages.analytics.nounTasks': 'tasks',

  // ── Spend panel + KPI tile ───────────────────────────────────────
  'pages.analytics.spendTitle': 'Spend',
  'pages.analytics.spendRange': '{from} – {to} (UTC)',
  'pages.analytics.spendLoading': 'Loading costs…',
  'pages.analytics.spendUnavailable': 'Costs could not be loaded.',
  'pages.analytics.spendSchedules': 'Scheduled tasks',
  'pages.analytics.spendRequestsSub': '{n} requests',
  'pages.analytics.spendRunsSub': '{n} runs',
  'pages.analytics.spendAgentsMissing': 'Agent costs are not included: {reason}',
  'pages.analytics.spendChartAria': 'Spend per day, agents and scheduled tasks',
  'pages.analytics.spendSearch': 'Search…',
  'pages.analytics.spendSearchLabel': 'Filter the spend table',
  'pages.analytics.spendEmpty': 'Nothing spent in this range.',
  'pages.analytics.spendSplit': 'Agents {a} · Tasks {s}',
  'pages.analytics.spendScheduledRow': 'Scheduled tasks (AI models)',
  'pages.analytics.spendScheduledRowSub': '{n} tasks · see By task',
  'pages.analytics.spendNote':
    'Agents report their own tokens and cost. Scheduled tasks use Bedrock token counts priced per model. Departments are each user’s current one. Days are UTC.',
  'pages.analytics.colShare': 'Share',
  'pages.analytics.kpiSpend': 'Spend',
  'pages.analytics.kpiSpendSub': 'agents + scheduled tasks',
  'pages.analytics.reviewFeedback': 'Review feedback',
  'pages.analytics.moreDetails': 'More details',
  'pages.analytics.moreDetailsHint': 'Usage heatmap, departments, user activity',

  // ── My Agents ────────────────────────────────────────────────────
  'pages.myAgents.title': 'My AI Agents',
  'pages.myAgents.subtitle': 'AI assistants you have access to',
  'pages.myAgents.loadFailed': 'Failed to load your agents',
  'pages.myAgents.emptyTitle': 'No Agents Available',
  'pages.myAgents.emptyBody': "You don't have access to any AI agents yet.",
  'pages.myAgents.emptyHint': 'Please contact your administrator to request access.',
  'pages.myAgents.searchPlaceholder': 'Search agents by name or description...',
  'pages.myAgents.searchLabel': 'Search agents',
  'pages.myAgents.noneFoundTitle': 'No agents found',
  'pages.myAgents.noneFoundBody': 'No agents match "{q}". Try a different search term.',
  'pages.myAgents.public': 'Public',
  'pages.myAgents.locked': 'Locked',
  'pages.myAgents.lockedButton': 'Access required',
  'pages.myAgents.lockedHint': 'You need access to use this agent. Ask the IT helpdesk to request it.',
  'pages.myAgents.defaultDescription': 'AI assistant ready to help',
  'pages.myAgents.lastUpdated': 'Last Updated:',
  'pages.myAgents.startChatting': 'Start Chatting',
  'pages.myAgents.unavailable': 'Currently Unavailable',
  'pages.myAgents.howToTitle': 'How to Get Started',
  'pages.myAgents.howTo1Before': 'Go to the ',
  'pages.myAgents.howTo1Chat': 'Chat',
  'pages.myAgents.howTo1After': ' page from the main menu and select an agent to start a conversation',
  'pages.myAgents.howTo2': 'Public agents are available to all Nesma Partners employees — no approval needed',
  'pages.myAgents.howTo3': 'Each agent has specialized knowledge tailored to a specific domain or workflow',
  'pages.myAgents.howTo4': 'Switch between agents anytime to tap into different areas of expertise',
  'pages.myAgents.accessTitle': 'Need Access to a Specific Agent?',
  'pages.myAgents.accessBody':
    'Agents marked Locked require approval. To use one, please submit a request ticket to the IT helpdesk and the appropriate access will be granted based on your role and business needs.',

  // ── Shared chat ──────────────────────────────────────────────────
  'pages.shared.docTitle': '{title} · Shared chat',
  'pages.shared.notSharedTitle': 'This chat is no longer shared',
  'pages.shared.loadFailedTitle': 'Could not load this chat',
  'pages.shared.notSharedBody': 'The link may be mistyped, or the owner stopped sharing it.',
  'pages.shared.loadFailedBody': 'Check your connection and try again.',
  'pages.shared.goToChats': 'Go to your chats',
  'pages.shared.sharedBy': 'Shared by {name}',
  'pages.shared.you': 'you',
  'pages.shared.asOf': 'As of {when}',
  'pages.shared.openInChat': 'Open in chat',
  'pages.shared.readOnly': 'Read-only',
  'pages.shared.noMessages': 'This conversation has no messages.',
  'pages.shared.aiAssistant': 'AI Assistant',
  'pages.shared.attachments': 'Attachments',
  'pages.shared.attachmentPrivate': 'Attachments stay private to the owner',
  'pages.shared.footer': 'This is a read-only copy. AI responses can be wrong — double-check before acting on them.',
};

const ar: Record<keyof typeof en, string> = {
  'pages.analytics.title': 'التحليلات',
  'pages.analytics.subtitle': 'استخدام المنصة واعتماد الوكلاء والتقييمات',
  'pages.analytics.updated': 'آخر تحديث {time}',
  'pages.analytics.timeRange': 'الفترة الزمنية',
  'pages.analytics.range7': 'آخر 7 أيام',
  'pages.analytics.range14': 'آخر 14 يوماً',
  'pages.analytics.range30': 'آخر 30 يوماً',
  'pages.analytics.range90': 'آخر 90 يوماً',
  'pages.analytics.range365': 'آخر سنة',
  'pages.analytics.refresh': 'تحديث التحليلات',
  'pages.analytics.refreshHint': 'تحديث (يتجاوز ذاكرة الخادم المؤقتة لمدة دقيقة)',
  'pages.analytics.loadFailed': 'تعذّر تحميل بيانات التحليلات.',
  'pages.analytics.noData': 'لا توجد تحليلات متاحة.',

  'pages.analytics.noMatches': 'لا توجد نتائج مطابقة لـ «{q}».',

  'pages.analytics.kpiActiveUsers': 'المستخدمون النشطون',
  'pages.analytics.kpiActiveUsersSub': 'من أصل {total} ({pct})',
  'pages.analytics.kpiMessages': 'الرسائل',
  'pages.analytics.kpiMessagesSub': '{n} ردود من الوكلاء',
  'pages.analytics.kpiSatisfaction': 'معدل الرضا',
  'pages.analytics.kpiSatisfactionSub': '{pos} إيجابي · {neg} سلبي',
  'pages.analytics.deltaNew': 'جديد',
  'pages.analytics.deltaPts': '{n} نقطة',
  'pages.analytics.deltaHint': 'التغيّر مقارنةً بالفترة السابقة بنفس المدة',
  'pages.analytics.deltaSr': ' مقارنةً بالفترة السابقة',

  'pages.analytics.activityTitle': 'النشاط عبر الزمن',
  'pages.analytics.activityAria': 'مخطط خطي للرسائل والجلسات والمستخدمين النشطين يومياً',
  'pages.analytics.noActivity': 'لا يوجد نشاط في هذه الفترة.',
  'pages.analytics.seriesMessages': 'الرسائل',
  'pages.analytics.seriesSessions': 'الجلسات',
  'pages.analytics.seriesActiveUsers': 'المستخدمون النشطون',
  'pages.analytics.heatmapTitle': 'أوقات الاستخدام',
  'pages.analytics.localTime': 'بتوقيتك المحلي',
  'pages.analytics.departmentsTitle': 'الاستخدام حسب القسم',
  'pages.analytics.departmentsAria': 'مخطط أعمدة للرسائل والمستخدمين النشطين لكل قسم',
  'pages.analytics.noDepartments': 'لا يوجد نشاط للأقسام في هذه الفترة.',
  'pages.analytics.deptActiveOf': '{active} من أصل {total} مستخدمين نشطين',

  'pages.analytics.adoptionTitle': 'اعتماد الوكلاء',
  'pages.analytics.noAgentsUsed': 'لم يُستخدم أي وكيل في هذه الفترة.',
  'pages.analytics.colAgent': 'الوكيل',
  'pages.analytics.colUser': 'المستخدم',
  'pages.analytics.colRole': 'الدور',
  'pages.analytics.colMessages': 'الرسائل',
  'pages.analytics.colSessions': 'الجلسات',
  'pages.analytics.colUsers': 'المستخدمون',
  'pages.analytics.colSatisfaction': 'معدل الرضا',
  'pages.analytics.colLastUsed': 'آخر استخدام',
  'pages.analytics.colLastAgent': 'آخر وكيل',
  'pages.analytics.colLastActivity': 'آخر نشاط',
  'pages.analytics.ratings': '{n} تقييم',
  'pages.analytics.nounAgents': 'وكلاء',
  'pages.analytics.nounUsers': 'مستخدمين',
  'pages.analytics.userActivityTitle': 'نشاط المستخدمين',
  'pages.analytics.allTime': 'كل الفترات',
  'pages.analytics.searchUsers': 'ابحث عن مستخدمين...',
  'pages.analytics.searchUsersLabel': 'البحث عن المستخدمين',
  'pages.analytics.loadingUsers': 'جارٍ تحميل نشاط المستخدمين',
  'pages.analytics.noUsersMatch': 'لا يوجد مستخدمون مطابقون لبحثك.',
  'pages.analytics.noUserData': 'لا توجد بيانات مستخدمين متاحة.',
  'pages.analytics.details': 'التفاصيل',
  'pages.analytics.detailsHint': 'عرض تفصيل استخدام الوكلاء',
  'pages.analytics.detailsAria': 'عرض استخدام الوكلاء للمستخدم {name}',
  'pages.analytics.roleAdmin': 'مسؤول',
  'pages.analytics.roleUser': 'مستخدم',

  'pages.analytics.usageModalTitle': 'استخدام الوكلاء — {name}',
  'pages.analytics.loadingAgentUsage': 'جارٍ تحميل استخدام الوكلاء',
  'pages.analytics.noAgentActivity': 'لا يوجد لهذا المستخدم أي نشاط مع الوكلاء بعد.',
  'pages.analytics.agentsUsed': 'الوكلاء المستخدمون',
  'pages.analytics.accessRevoked': 'تم سحب الصلاحية',
  'pages.analytics.notUsedYet': 'لم يُستخدم بعد',
  'pages.analytics.usageMeta': '{messages} رسالة · {sessions} جلسة',
  'pages.analytics.lastUsed': 'آخر استخدام',

  'pages.analytics.heatmapEmpty': 'لا توجد رسائل في هذه الفترة.',
  'pages.analytics.heatmapBusiest': 'وقت الذروة:',
  'pages.analytics.heatmapPeakCount': '({n} رسالة)',
  'pages.analytics.heatmapCaption': 'الرسائل حسب يوم الأسبوع وساعة اليوم',
  'pages.analytics.heatmapDay': 'اليوم',
  'pages.analytics.heatmapCell': '{day} {hour} — {n} رسالة',
  'pages.analytics.heatmapLess': 'أقل',
  'pages.analytics.heatmapMore': 'أكثر',

  'pages.analytics.alertsTitle': 'تنبيهات الاستخدام',
  'pages.analytics.alertNearLimit': 'تم استخدام {used} من أصل {limit} رسالة اليوم (بتوقيت UTC)',
  'pages.analytics.alertSpike': '{n} رسالة خلال 24 ساعة — {ratio}× المعدل المعتاد البالغ {avg} يومياً',
  'pages.analytics.alertSpikeNoBaseline': '{n} رسالة خلال 24 ساعة دون أي نشاط سابق في هذه الفترة',
  'pages.analytics.kindAgent': 'وكيل',
  'pages.analytics.kindUser': 'مستخدم',

  'pages.analytics.costsUnavailable': 'تعذّر تحميل تقارير تكاليف الوكلاء.',
  'pages.analytics.costsNotConfigured': 'تقارير تكاليف الوكلاء غير مُعدّة (AgentCosts:Bucket).',
  'pages.analytics.costsAccessDenied': 'لا يملك الخادم صلاحية قراءة تقارير تكاليف الوكلاء في S3.',
  'pages.analytics.costsNoteSar': ' يُحوَّل إلى الريال السعودي بسعر {rate} ريال لكل دولار.',
  'pages.analytics.costsCurrency': 'العملة',
  'pages.analytics.costsByAgent': 'حسب الوكيل',
  'pages.analytics.costsByUser': 'حسب المستخدم',
  'pages.analytics.costsByDepartment': 'حسب القسم',
  'pages.analytics.colDepartment': 'القسم',
  'pages.analytics.nounDepartments': 'أقسام',
  'pages.analytics.spendNoDepartment': 'بدون قسم',
  'pages.analytics.spendDepartmentSub': '{n} مستخدمين لديهم إنفاق',
  'pages.analytics.costsByModel': 'حسب النموذج',
  'pages.analytics.colModel': 'النموذج',
  'pages.analytics.colAgents': 'الوكلاء',
  'pages.analytics.colRequests': 'الطلبات',
  'pages.analytics.colTokens': 'الرموز',
  'pages.analytics.colCost': 'التكلفة',
  'pages.analytics.nounModels': 'نماذج',

  'pages.analytics.schedCostsUnpriced': 'لا يوجد سعر محدد لـ {models}. تُحتسب رموزها لكن تكلفتها 0. أضفها ضمن BedrockModels:Pricing.',
  'pages.analytics.schedCostsByTask': 'حسب المهمة',
  'pages.analytics.colTask': 'المهمة',
  'pages.analytics.nounTasks': 'مهام',

  'pages.analytics.spendTitle': 'الإنفاق',
  'pages.analytics.spendRange': '{from} – {to} (UTC)',
  'pages.analytics.spendLoading': 'جارٍ تحميل التكاليف…',
  'pages.analytics.spendUnavailable': 'تعذّر تحميل التكاليف.',
  'pages.analytics.spendSchedules': 'المهام المجدولة',
  'pages.analytics.spendRequestsSub': '{n} طلب',
  'pages.analytics.spendRunsSub': '{n} تشغيل',
  'pages.analytics.spendAgentsMissing': 'تكاليف الوكلاء غير مشمولة: {reason}',
  'pages.analytics.spendChartAria': 'الإنفاق اليومي للوكلاء والمهام المجدولة',
  'pages.analytics.spendSearch': 'بحث…',
  'pages.analytics.spendSearchLabel': 'تصفية جدول الإنفاق',
  'pages.analytics.spendEmpty': 'لا يوجد إنفاق في هذه الفترة.',
  'pages.analytics.spendSplit': 'الوكلاء {a} · المهام {s}',
  'pages.analytics.spendScheduledRow': 'المهام المجدولة (نماذج الذكاء الاصطناعي)',
  'pages.analytics.spendScheduledRowSub': '{n} مهام · راجع «حسب المهمة»',
  'pages.analytics.spendNote':
    'يُبلغ الوكلاء عن رموزهم وتكلفتهم بأنفسهم. المهام المجدولة تستخدم عدد رموز Bedrock مُسعَّراً لكل نموذج. القسم هو القسم الحالي لكل مستخدم. الأيام بتوقيت UTC.',
  'pages.analytics.colShare': 'الحصة',
  'pages.analytics.kpiSpend': 'الإنفاق',
  'pages.analytics.kpiSpendSub': 'الوكلاء + المهام المجدولة',
  'pages.analytics.reviewFeedback': 'مراجعة التقييمات',
  'pages.analytics.moreDetails': 'مزيد من التفاصيل',
  'pages.analytics.moreDetailsHint': 'خريطة الاستخدام، الأقسام، نشاط المستخدمين',

  'pages.myAgents.title': 'وكلاء الذكاء الاصطناعي',
  'pages.myAgents.subtitle': 'المساعدون الذكيون المتاحون لك',
  'pages.myAgents.loadFailed': 'تعذّر تحميل وكلائك',
  'pages.myAgents.emptyTitle': 'لا يوجد وكلاء متاحون',
  'pages.myAgents.emptyBody': 'ليس لديك صلاحية الوصول إلى أي وكيل ذكاء اصطناعي بعد.',
  'pages.myAgents.emptyHint': 'يرجى التواصل مع المسؤول لطلب الصلاحية.',
  'pages.myAgents.searchPlaceholder': 'ابحث عن الوكلاء بالاسم أو الوصف...',
  'pages.myAgents.searchLabel': 'البحث عن الوكلاء',
  'pages.myAgents.noneFoundTitle': 'لم يتم العثور على وكلاء',
  'pages.myAgents.noneFoundBody': 'لا يوجد وكلاء مطابقون لـ «{q}». جرّب كلمة بحث أخرى.',
  'pages.myAgents.public': 'عام',
  'pages.myAgents.locked': 'مقفل',
  'pages.myAgents.lockedButton': 'يتطلب صلاحية',
  'pages.myAgents.lockedHint': 'تحتاج إلى صلاحية لاستخدام هذا الوكيل. اطلبها من مكتب الدعم الفني.',
  'pages.myAgents.defaultDescription': 'مساعد ذكي جاهز للمساعدة',
  'pages.myAgents.lastUpdated': 'آخر تحديث:',
  'pages.myAgents.startChatting': 'ابدأ المحادثة',
  'pages.myAgents.unavailable': 'غير متاح حالياً',
  'pages.myAgents.howToTitle': 'كيف تبدأ',
  'pages.myAgents.howTo1Before': 'انتقل إلى صفحة ',
  'pages.myAgents.howTo1Chat': 'المحادثة',
  'pages.myAgents.howTo1After': ' من القائمة الرئيسية واختر وكيلاً لبدء المحادثة',
  'pages.myAgents.howTo2': 'الوكلاء العامّون متاحون لجميع موظفي نسما وشركاه — دون الحاجة إلى موافقة',
  'pages.myAgents.howTo3': 'يمتلك كل وكيل معرفة متخصصة في مجال أو سير عمل محدد',
  'pages.myAgents.howTo4': 'يمكنك التبديل بين الوكلاء في أي وقت للاستفادة من مجالات خبرة مختلفة',
  'pages.myAgents.accessTitle': 'تحتاج إلى الوصول إلى وكيل معيّن؟',
  'pages.myAgents.accessBody':
    'الوكلاء الذين يحملون علامة «مقفل» يتطلبون موافقة. لاستخدام أحدهم، يرجى تقديم طلب إلى مكتب الدعم الفني، وسيتم منح الصلاحية المناسبة وفقاً لدورك واحتياجات العمل.',

  'pages.shared.docTitle': '{title} · محادثة مشتركة',
  'pages.shared.notSharedTitle': 'لم تعد هذه المحادثة مشتركة',
  'pages.shared.loadFailedTitle': 'تعذّر تحميل هذه المحادثة',
  'pages.shared.notSharedBody': 'ربما يكون الرابط غير صحيح، أو أوقف المالك مشاركته.',
  'pages.shared.loadFailedBody': 'تحقق من اتصالك وحاول مرة أخرى.',
  'pages.shared.goToChats': 'الانتقال إلى محادثاتك',
  'pages.shared.sharedBy': 'شاركها {name}',
  'pages.shared.you': 'أنت',
  'pages.shared.asOf': 'حتى {when}',
  'pages.shared.openInChat': 'فتح في المحادثة',
  'pages.shared.readOnly': 'للقراءة فقط',
  'pages.shared.noMessages': 'لا تحتوي هذه المحادثة على رسائل.',
  'pages.shared.aiAssistant': 'المساعد الذكي',
  'pages.shared.attachments': 'المرفقات',
  'pages.shared.attachmentPrivate': 'تبقى المرفقات خاصة بالمالك',
  'pages.shared.footer': 'هذه نسخة للقراءة فقط. قد تكون ردود الذكاء الاصطناعي غير دقيقة — تحقّق منها قبل الاعتماد عليها.',
};

const pages = { en, ar };
export default pages;
